import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useRequest from '../hooks/useRequest';

export type DeviceType = 'TELEFON' | 'TABLET' | 'TV' | 'LAPTOP';

export interface ElectronicsTemplateItem {
    id: number;
    denumire: string;
    isBool: boolean;
    boolValue: boolean;
    valoare: string;
    hint: string;
}

interface ElectronicsModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (denominareObiect: string, pretAmanet: string) => void;
}

const ElectronicsModal = ({ open, onClose, onSave }: ElectronicsModalProps) => {
    const { t } = useTranslation();
    const request = useRequest();

    const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType>('TELEFON');
    const [templateItems, setTemplateItems] = useState<ElectronicsTemplateItem[]>([]);
    const [loading, setLoading] = useState(false);

    // Device type options matching Delphi implementation
    const deviceTypes: { value: DeviceType; label: string }[] = [
        { value: 'TELEFON', label: t('electronics.deviceTypes.phone', 'Phone') },
        { value: 'TABLET', label: t('electronics.deviceTypes.tablet', 'Tablet') },
        { value: 'TV', label: t('electronics.deviceTypes.tv', 'TV') },
        { value: 'LAPTOP', label: t('electronics.deviceTypes.laptop', 'Laptop') },
    ];

    const resetTemplateValues = useCallback(async () => {
        try {
            console.log('Resetting template values...');
            await request({
                path: '/electronics-annex/reset-templates',
                method: 'POST',
            });
            console.log('Template values reset successfully');
        } catch (error) {
            console.error('Error resetting template values:', error);
            throw error;
        }
    }, [request]);

    const loadTemplates = useCallback(async () => {
        try {
            console.log('Loading templates for device type:', selectedDeviceType);
            const response = await request<{ templates: ElectronicsTemplateItem[] }>({
                path: `/electronics-annex/templates/${selectedDeviceType}`,
                method: 'GET',
            });

            console.log('Templates response:', response);

            if (response?.templates) {
                console.log('Setting template items:', response.templates);
                setTemplateItems(response.templates);
            } else {
                console.log('No templates in response');
                setTemplateItems([]);
            }
        } catch (error) {
            console.error('Error loading templates:', error);
            setTemplateItems([]);
            throw error;
        }
    }, [request, selectedDeviceType]);

    // Load templates when device type changes or when modal opens
    useEffect(() => {
        if (open && selectedDeviceType) {
            const loadTemplatesWithReset = async () => {
                try {
                    setLoading(true);
                    console.log(
                        'Starting template reset and load for device type:',
                        selectedDeviceType,
                    );

                    // First reset all values (like Delphi does)
                    await resetTemplateValues();
                    // Then load the templates for the selected device
                    await loadTemplates();
                } catch (error) {
                    console.error('Error during template reset and load:', error);
                } finally {
                    setLoading(false);
                }
            };
            loadTemplatesWithReset();
        }
    }, [selectedDeviceType, open, resetTemplateValues, loadTemplates]);

    const handleTemplateValueChange = async (
        templateId: number,
        isBool: boolean,
        value: string | boolean,
    ) => {
        try {
            console.log('Updating template value:', { templateId, isBool, value });
            // Update backend immediately (like Delphi post on grid exit)
            await request({
                path: `/electronics-annex/templates/${templateId}`,
                method: 'PUT',
                params: { isBool, value },
            });
            console.log('Template value updated successfully');

            // Update local state
            setTemplateItems(prev =>
                prev.map(item => {
                    if (item.id === templateId) {
                        if (isBool) {
                            return { ...item, boolValue: Boolean(value), valoare: '' };
                        } else {
                            return { ...item, valoare: String(value), boolValue: false };
                        }
                    }
                    return item;
                }),
            );
        } catch (error) {
            console.error('Error updating template value:', error);
        }
    };

    const handleSave = async () => {
        try {
            // Get the "Denumire obiect" and "Pret AMANET" values from templates
            const denumireObiectItem = templateItems.find(
                item =>
                    item.denumire.toLowerCase().includes('denumire obiect') ||
                    item.denumire.toLowerCase().includes('marca') ||
                    item.denumire.toLowerCase().includes('model'),
            );

            const pretAmanetItem = templateItems.find(
                item =>
                    item.denumire.toLowerCase().includes('pret amanet') ||
                    item.denumire.toLowerCase().includes('pret'),
            );

            const denumireObiect = denumireObiectItem?.valoare ?? '';
            const pretAmanet = pretAmanetItem?.valoare ?? '0';

            // Build the full object name: "<device type> + <object name>"
            const fullObjectName = `${selectedDeviceType} ${denumireObiect}`.trim();

            // Call the save callback with the extracted values
            onSave(fullObjectName, pretAmanet);

            // Close the modal
            onClose();
        } catch (error) {
            console.error('Error saving electronics data:', error);
        }
    };

    const handleCancel = () => {
        // Simply close without saving (like Delphi cancel behavior)
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        minHeight: '500px',
                        maxHeight: '80vh',
                    },
                },
            }}
        >
            <DialogTitle>
                {t('electronics.modal.title', 'Electronics/Other Objects Characteristics')}
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>{t('electronics.deviceType', 'Device Type')}</InputLabel>
                        <Select
                            value={selectedDeviceType}
                            label={t('electronics.deviceType', 'Device Type')}
                            onChange={e => setSelectedDeviceType(e.target.value as DeviceType)}
                            disabled={loading}
                        >
                            {deviceTypes.map(type => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {loading && <Typography>{t('common.loading', 'Loading...')}</Typography>}

                {!loading && templateItems.length === 0 && (
                    <Typography color="textSecondary">
                        {t(
                            'electronics.noTemplates',
                            'No templates available for this device type',
                        )}
                    </Typography>
                )}

                {!loading && templateItems.length > 0 && (
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 200 }}>
                                        {t('electronics.characteristic', 'Characteristic')}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 100 }}>
                                        {t('electronics.value', 'Value')}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 80 }}>
                                        {t('electronics.boolean', 'Yes/No')}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>
                                        {t('electronics.hint', 'Hint')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {templateItems.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <Typography variant="body2">{item.denumire}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                value={item.valoare}
                                                disabled={item.isBool}
                                                onChange={e =>
                                                    handleTemplateValueChange(
                                                        item.id,
                                                        false,
                                                        e.target.value,
                                                    )
                                                }
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        backgroundColor: item.isBool
                                                            ? '#f5f5f5'
                                                            : 'white',
                                                    },
                                                }}
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Checkbox
                                                checked={item.boolValue}
                                                disabled={!item.isBool}
                                                onChange={e =>
                                                    handleTemplateValueChange(
                                                        item.id,
                                                        true,
                                                        e.target.checked,
                                                    )
                                                }
                                                sx={{
                                                    opacity: item.isBool ? 1 : 0.3,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">
                                                {item.hint}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleCancel} variant="outlined" disabled={loading}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                    {t('common.save', 'Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ElectronicsModal;
